
import React from 'react';
import { Coin } from '../types.ts';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';

interface HomeProps {
  coins: Coin[];
  onCoinClick: (coin: Coin) => void;
}

const Home: React.FC<HomeProps> = ({ coins, onCoinClick }) => {
  const topTwo = coins.slice(0, 2);
  const others = coins.slice(2, 7);

  return (
    <div className="px-5 pt-12 pb-6 space-y-8 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <img 
              src="https://picsum.photos/seed/user123/100/100" 
              className="w-12 h-12 rounded-full border-2 border-blue-500/30 object-cover" 
              alt="User" 
            />
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-[#0D0D0D] rounded-full"></div>
          </div>
          <div>
            <p className="text-zinc-500 text-xs font-medium">Good morning,</p>
            <h2 className="text-white font-bold text-lg">Jenny Wilson</h2>
          </div>
        </div>
        <button className="relative p-2.5 bg-zinc-900/80 rounded-full border border-zinc-800 active:scale-90 transition-transform">
          <svg className="w-5 h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full ring-2 ring-zinc-900"></span>
        </button>
      </div>

      {/* Banner */}
      <div className="group relative bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-600 rounded-[32px] p-7 overflow-hidden shadow-2xl shadow-blue-500/20 active:scale-[0.98] transition-all">
        <div className="relative z-10 space-y-3">
          <div className="inline-flex items-center px-2 py-1 rounded-full bg-white/10 backdrop-blur-md text-[10px] text-white font-bold uppercase tracking-widest">
            Special Offer
          </div>
          <h3 className="text-white text-2xl font-bold leading-tight">Start Trading<br/>with $0 Fees</h3>
          <p className="text-blue-100/80 text-sm max-w-[180px]">Join over 2 million investors worldwide.</p>
          <button className="mt-2 px-6 py-2.5 bg-white text-blue-600 font-bold rounded-2xl text-sm shadow-lg hover:shadow-xl transition-all">
            Get Started
          </button>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-[-30px] right-[-30px] w-48 h-48 bg-white/10 rounded-full blur-3xl group-hover:scale-110 transition-transform"></div>
        <div className="absolute bottom-[-50px] left-20 w-32 h-32 bg-indigo-400/20 rounded-full blur-2xl"></div>
        <div className="absolute top-6 right-8 opacity-40">
            <svg className="w-24 h-24 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
        </div>
      </div>

      {/* Featured Coins */}
      <div className="space-y-4">
        <div className="flex justify-between items-end px-1">
          <h4 className="text-white font-bold text-lg">Featured Coins</h4>
          <button className="text-blue-500 text-xs font-bold">See all</button>
        </div>
        <div className="flex space-x-4 overflow-x-auto hide-scrollbar pb-2 snap-x snap-mandatory">
          {topTwo.map((coin) => (
            <div 
              key={coin.id} 
              onClick={() => onCoinClick(coin)}
              className="min-w-[170px] flex-1 bg-zinc-900/40 border border-zinc-800/60 backdrop-blur-sm p-5 rounded-[28px] space-y-4 cursor-pointer hover:border-blue-500/30 active:scale-95 transition-all snap-start"
            >
              <div className="flex items-center space-x-2">
                <img src={coin.image} className="w-9 h-9 rounded-full shadow-lg shadow-black/20" alt={coin.symbol} />
                <div>
                    <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">{coin.symbol}</p>
                    <p className="text-white font-bold text-sm">${coin.current_price.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="h-12 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={coin.sparkline_in_7d?.price.map((val, i) => ({ val, i }))}>
                    <Line 
                        type="monotone" 
                        dataKey="val" 
                        stroke={coin.price_change_percentage_24h >= 0 ? "#10B981" : "#EF4444"} 
                        strokeWidth={2} 
                        dot={false} 
                    />
                    <YAxis hide domain={['auto', 'auto']} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className={`flex items-center text-[10px] font-bold space-x-1 p-1 rounded-lg w-fit ${coin.price_change_percentage_24h >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
                <span>{coin.price_change_percentage_24h >= 0 ? '↑' : '↓'}</span>
                <span>{Math.abs(coin.price_change_percentage_24h).toFixed(2)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Market Activity */}
      <div className="space-y-4">
        <h4 className="text-white font-bold text-lg px-1">Market Activity</h4>
        <div className="space-y-3">
          {others.map((coin) => (
            <div 
                key={coin.id} 
                onClick={() => onCoinClick(coin)}
                className="flex items-center justify-between p-4 bg-zinc-900/20 border border-zinc-800/40 rounded-[24px] cursor-pointer hover:bg-zinc-800/30 transition-all active:scale-[0.98]"
            >
              <div className="flex items-center space-x-3">
                <div className="relative">
                    <img src={coin.image} className="w-11 h-11 rounded-full shadow-md shadow-black/20" alt={coin.name} />
                </div>
                <div>
                  <h5 className="text-white font-bold text-sm">{coin.name}</h5>
                  <div className="flex items-center space-x-2">
                    <p className="text-zinc-500 text-xs uppercase">{coin.symbol}</p>
                    <span className={`text-[10px] font-bold ${coin.price_change_percentage_24h >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {coin.price_change_percentage_24h >= 0 ? '+' : ''}{coin.price_change_percentage_24h.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right flex flex-col items-end space-y-1">
                <p className="text-white font-bold text-sm">${coin.current_price.toLocaleString()}</p>
                <div className="h-4 w-12 opacity-50">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={coin.sparkline_in_7d?.price.slice(-10).map((val, i) => ({ val, i }))}>
                            <Line type="monotone" dataKey="val" stroke={coin.price_change_percentage_24h >= 0 ? "#10B981" : "#EF4444"} strokeWidth={1} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Home;
